import { useInfiniteQuery } from '@tanstack/react-query';
import { EventSchema, type Event } from '@casetrace/shared';
import type { FilterState } from '../pages/case-room/filter-panel';
import { buildSearchParams } from '../pages/case-room/filters/build-search-params';
import { EVENTS_COLLECTION, typesenseClient } from '../lib/typesense';

const PAGE_SIZE = 30;

interface TimelineSearchHit {
  document: {
    id: string;
    caseId: string;
    timestampIso: string;
    type: Event['type'];
    source: string;
    title: string;
    contentPreview?: string;
    hasAttachments?: boolean;
    sourceItemId: string;
  };
}

interface TimelineSearchResponse {
  hits?: TimelineSearchHit[];
  found?: number;
  page?: number;
}

export interface TimelineSearchPage {
  events: Event[];
  found: number;
}

function eventContainsText(event: Event, term: string): boolean {
  const haystack = `${event.title}\n${event.contentPreview ?? ''}`.toLowerCase();
  return haystack.includes(term.toLowerCase());
}

function createFilterKey(filters: FilterState): string {
  const types = Array.from(filters.types).sort();
  return JSON.stringify({
    types,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    hasAttachments: filters.hasAttachments,
    include: filters.include,
    exclude: filters.exclude,
  });
}

async function fetchTimelineSearchPage(
  caseId: string,
  filters: FilterState,
  page: number,
): Promise<TimelineSearchPage> {
  if (!typesenseClient) return { events: [], found: 0 };
  const searchParams = buildSearchParams(caseId, filters);
  const raw = await typesenseClient.collections(EVENTS_COLLECTION).documents().search({
    q: searchParams.q,
    query_by: searchParams.query_by,
    filter_by: searchParams.filter_by,
    sort_by: 'timestamp:asc',
    per_page: PAGE_SIZE,
    page,
  });

  const response = raw as TimelineSearchResponse;
  const events = (response.hits ?? []).map(({ document }) =>
    EventSchema.parse({
      id: document.id,
      caseId: document.caseId,
      timestamp: document.timestampIso,
      type: document.type,
      source: document.source,
      title: document.title,
      contentPreview: document.contentPreview,
      metadata: {},
      sourceItemId: document.sourceItemId,
    }),
  );

  const filteredEvents =
    searchParams.additionalTextIncludes.length > 0
      ? events.filter((event) =>
          searchParams.additionalTextIncludes.every((term) => eventContainsText(event, term)),
        )
      : events;

  return { events: filteredEvents, found: response.found ?? 0 };
}

export function useTimelineSearch(caseId: string, filters: FilterState, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['timeline-search', caseId, createFilterKey(filters)] as const,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      fetchTimelineSearchPage(caseId, filters, pageParam),
    initialPageParam: 1,
    enabled: enabled && Boolean(caseId) && Boolean(typesenseClient),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.events.length, 0);
      if (loaded >= lastPage.found || lastPage.found === 0) return undefined;
      return allPages.length + 1;
    },
  });
}
